import React, {memo, useCallback, FC} from "react";
import {useDispatch, useSelector} from "react-redux";
import {navigate} from "@reach/router";
import {RootState} from "../../../../store/rootReducer";
import {extraActionsMenuSelector} from "./extraActionsMenu.selector";
import {cardViewActions} from "../../cardView.slice";
import {cardsActions} from "../../../cards.slice";
import {
    CardState,
    LlmProvider,
    MAX_REGENERATE_INSTRUCTIONS_LENGTH,
    MAX_REGENERATE_TEXT_LENGTH,
    markupFormatter,
} from "../../../../common";
import {regenerateSeedFromMarkup} from "../../../regenerate/regenerateSeed";
import {useMarkCardDialog} from "../../../mark/useMarkCardDialog";
import {markInstructions} from "../../../mark/markInstructions";
import styled from "styled-components";
import { ThemeProps } from "../../../../styles/theme";
import { useConfirmDialog } from "../../../../components/ConfirmDialogContext";

interface Props {
    cardId: string;
}

const Root = styled.div`
    position: fixed;
    width: 100%;
    height: 100%;
`;

const UnderLayer = styled.div`
    width: 100%;
    height: 100%;
`;

const MenuLayer = styled.div<ThemeProps>`
    position: absolute;
    right: 10px;
    top: 43px;
    padding: 10px 20px;
    background-color: ${props => props.theme.primaryBackground};
    border: ${props => props.theme.primaryBorder} 1px solid;
    display: flex;
    flex-direction: column;
`;

const MenuItem = styled.div`
    margin-top: 10px;
`;

const MenuSeparator = styled.div<ThemeProps>`
    margin-top: 15px;
    height: 2px;
    width: 100%;
    background-color: ${props => props.theme.primaryBorder};
`;

const ExtraActionsMenuComponent: FC<Props> = (props) => {
    const {isMenuOpen, isReverting} = useSelector(extraActionsMenuSelector);
    const {cardId} = props;
    const isGenerating = useSelector((state: RootState) => state.cards[cardId]?.generation?.status === 'running');
    const canChangeContent = !isGenerating && !isReverting;
    const hasBackup = useSelector((state: RootState) => state.cards[cardId]?.hasBackup === true);
    const markup = useSelector((state: RootState) => state.cards[cardId]?.markup);
    const mark = useSelector((state: RootState) => state.cards[cardId]?.mark);
    const openMarkDialog = useMarkCardDialog();
    const hasSubCards = markup !== undefined && markupFormatter.parse(markup).hasSubCards;
    const dispatch = useDispatch();

    // Используем глобальный контекст подтверждения
    const { openConfirmDialog, openPromptDialog } = useConfirmDialog();

    const clickOutsideHandler = useCallback(() => {
        dispatch(cardViewActions.closeExtraActionsMenu());
    }, [dispatch]);

    const practiceHandler = useCallback(() => {
        dispatch(cardViewActions.closeExtraActionsMenu());
        window.open(`/practice/${encodeURIComponent(cardId)}`, '_blank', 'noopener,noreferrer');
    }, [cardId, dispatch]);

    const isWholeCardMarked = mark?.wholeCard === true;

    const markHandler = useCallback(() => {
        dispatch(cardViewActions.closeExtraActionsMenu());
        openMarkDialog(cardId, mark);
    }, [cardId, dispatch, mark, openMarkDialog]);

    const unmarkHandler = useCallback(() => {
        dispatch(cardViewActions.closeExtraActionsMenu());
        dispatch(cardsActions.unmarkWholeCard(cardId));
    }, [cardId, dispatch]);

    const deleteHandler = useCallback(() => {
        // Используем глобальный диалог подтверждения
        dispatch(cardViewActions.closeExtraActionsMenu());
        openConfirmDialog(
            'Delete Card',
            'Are you sure you want to delete this card?',
            () => {
                dispatch(cardsActions.deleteCard(cardId!));
                navigate(`/search`);
            }
        );
    }, [cardId, dispatch, openConfirmDialog]);

    const futureHandler = useCallback(() => {
        dispatch(cardViewActions.closeExtraActionsMenu());
        openConfirmDialog(
            'Move to Future',
            'Are you sure you want to move this card to Future state?',
            () => {
                dispatch(cardsActions.moveToState({cardId, state: CardState.Future}));
            }
        );
    }, [cardId, dispatch, openConfirmDialog]);

    const memorizerHandler = useCallback(() => {
        dispatch(cardViewActions.closeExtraActionsMenu());
        openConfirmDialog(
            'Move to Memorizer',
            'Are you sure you want to move this card to Memorizer state?',
            () => {
                dispatch(cardsActions.moveToState({cardId, state: CardState.Memorizer}));
            }
        );
    }, [cardId, dispatch, openConfirmDialog]);

    const knownHandler = useCallback(() => {
        dispatch(cardViewActions.closeExtraActionsMenu());
        openConfirmDialog(
            'Move to Known',
            'Are you sure you want to move this card to Known state?',
            () => {
                dispatch(cardsActions.moveToState({cardId, state: CardState.Known}));
            }
        );
    }, [cardId, dispatch, openConfirmDialog]);

    const regenerateHandler = useCallback(() => {
        dispatch(cardViewActions.closeExtraActionsMenu());
        openPromptDialog({
            title: 'Regenerate Card',
            message: 'This will replace the card content using AI. Continue?',
            field: {
                label: 'Word or expression to generate from',
                defaultValue: regenerateSeedFromMarkup(markup),
                maxLength: MAX_REGENERATE_TEXT_LENGTH,
                singleLine: true,
            },
            extraField: {
                label: 'Additional instructions (optional)',
                placeholder: 'For example: use only the meaning “обновлять”, not “ремонтировать”.',
                defaultValue: markInstructions(mark),
                maxLength: MAX_REGENERATE_INSTRUCTIONS_LENGTH,
            },
            confirmLabel: 'Regenerate',
            select: {
                label: 'LLM provider',
                defaultValue: 'deepseek',
                options: [
                    {value: 'deepseek', label: 'DeepSeek'},
                    {value: 'chatgpt', label: 'ChatGPT'},
                ],
            },
            onConfirm: ({text, extraText, selectValue}) => {
                const trimmedText = text.trim();
                const trimmedInstructions = extraText.trim();
                dispatch(cardsActions.regenerateCard({
                    cardId,
                    text: trimmedText || undefined,
                    instructions: trimmedInstructions || undefined,
                    provider: selectValue as LlmProvider,
                }));
            },
        });
    }, [cardId, dispatch, mark, markup, openPromptDialog]);

    const invertHandler = useCallback(() => {
        dispatch(cardViewActions.closeExtraActionsMenu());
        openConfirmDialog(
            'Invert Card',
            'This will regenerate the card in the opposite direction using AI. Continue?',
            () => {
                dispatch(cardsActions.invertCard(cardId));
            }
        );
    }, [cardId, dispatch, openConfirmDialog]);

    const revertHandler = useCallback(() => {
        dispatch(cardViewActions.closeExtraActionsMenu());
        openConfirmDialog(
            'Revert Card',
            'This will restore the card to its version before the last regenerate or invert. Continue?',
            () => {
                dispatch(cardsActions.revertCard(cardId));
            }
        );
    }, [cardId, dispatch, openConfirmDialog]);

    return !isMenuOpen
        ? null
        : (
            <>
                <Root>
                    <UnderLayer onClick={clickOutsideHandler}/>
                    <MenuLayer>
                        {hasSubCards && (
                            <>
                                <MenuItem data-testid="card-practice" onClick={practiceHandler}>Practice</MenuItem>
                                <MenuSeparator/>
                            </>
                        )}
                        <MenuItem onClick={memorizerHandler}>Move to Memorizer</MenuItem>
                        <MenuItem onClick={futureHandler}>Move to Future</MenuItem>
                        <MenuItem onClick={knownHandler}>Move to Known</MenuItem>
                        <MenuSeparator/>
                        {canChangeContent && (
                            <MenuItem data-testid="card-regenerate" onClick={regenerateHandler}>Regenerate</MenuItem>
                        )}
                        {canChangeContent && (
                            <MenuItem data-testid="card-invert" onClick={invertHandler}>Invert</MenuItem>
                        )}
                        {canChangeContent && hasBackup && (
                            <MenuItem data-testid="card-revert" onClick={revertHandler}>Undo last generation</MenuItem>
                        )}
                        <MenuItem data-testid="card-mark" onClick={markHandler}>
                            {isWholeCardMarked ? 'Edit card mark' : 'Mark card for regeneration'}
                        </MenuItem>
                        {isWholeCardMarked && (
                            <MenuItem data-testid="card-unmark" onClick={unmarkHandler}>Remove card mark</MenuItem>
                        )}
                        <MenuItem data-testid="card-delete" onClick={deleteHandler}>Delete</MenuItem>
                    </MenuLayer>
                </Root>

                {/* Удаляем рендер диалога, так как он теперь глобальный */}
            </>
        );
};

ExtraActionsMenuComponent.displayName = 'ExtraActionsMenuComponent';

export const ExtraActionsMenu = memo(ExtraActionsMenuComponent);
ExtraActionsMenu.displayName = 'ExtraActionsMenu';
